/**
 * Foxtrot Counter - block animation
 */
class FoxtrotCounter {
    constructor(options = {}) {
        this.blockSelector = '.foxtrot-block.counter-block';
        this.itemSelector = '.foxtrot-animate-num';
        
        // Settings
        this.thresholdTrigger = options.thresholdTrigger || 0.8;
        this.thresholdReset = options.thresholdReset || 0.1;
        this.bufferTime = options.bufferTime || 300;
        this.defaultDuration = options.duration || 1500;

        // Memory Management
        this.activeTimers = new Map(); // Stores setTimeout IDs for buffering
        this.activeFrames = new Map(); // Stores requestAnimationFrame IDs
        
        this.init();
    }

    init() {
        const blocks = document.querySelectorAll(this.blockSelector);
        if (!blocks.length) return;

        const observerOptions = {
            root: null,
            threshold: [this.thresholdReset, this.thresholdTrigger]
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio >= this.thresholdTrigger) {
                    this.animateBlock(entry.target);
                } else if (entry.intersectionRatio < this.thresholdReset) {
                    this.resetBlock(entry.target);
                }
            });
        }, observerOptions);

        blocks.forEach(block => {
            // Pre-calculate data for every counter inside to save CPU cycles during animation
            block.querySelectorAll(this.itemSelector).forEach(el => this.prepareElement(el));
            observer.observe(block);
        });
    }

    /**
     * Parse and store numerical data once on load.
     * Prevents expensive string splitting/parsing during 60fps animation.
     */
    prepareElement(el) {
        const targetStr = el.getAttribute('data-target') || "0";
        const targetNum = parseFloat(targetStr);
        const isDecimal = targetStr.includes('.');
        const decimals = isDecimal ? targetStr.split('.')[1].length : 0;

        // Cache properties on the element itself
        el._foxtrotData = {
            target: targetNum,
            decimals: decimals,
            raw: targetStr
        };

        // Initial State
        el.innerText = targetNum.toFixed(decimals).replace(/\d/g, '0');
    }

    resetBlock(block) {
        // 1. Clear sequential buffer timers
        if (this.activeTimers.has(block)) {
            this.activeTimers.get(block).forEach(id => clearTimeout(id));
            this.activeTimers.delete(block);
        }

        // 2. Stop all active animations and reset text
        block.querySelectorAll(this.itemSelector).forEach(el => {
            if (this.activeFrames.has(el)) {
                cancelAnimationFrame(this.activeFrames.get(el));
                this.activeFrames.delete(el);
            }
            
            el.classList.remove('is-animating');
            if (el._foxtrotData) {
                el.innerText = el._foxtrotData.target.toFixed(el._foxtrotData.decimals).replace(/\d/g, '0');
            }
        });
    }

    animateBlock(block) {
        const counters = block.querySelectorAll(this.itemSelector);
        const blockDuration = parseFloat(block.getAttribute('data-duration')) * 1000 || this.defaultDuration;
        const timers = [];

        counters.forEach((el, index) => {
            // Skip if already playing
            if (el.classList.contains('is-animating')) return;
            el.classList.add('is-animating');

            // Apply buffer delay sequentially
            const timerId = setTimeout(() => {
                this.runCounter(el, blockDuration);
            }, index * this.bufferTime);

            timers.push(timerId);
        });

        this.activeTimers.set(block, timers);
    }

    runCounter(el, duration) {
        const data = el._foxtrotData;
        if (!data) return;

        let startTime = null;

        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            
            // Professional easeOutQuart for smooth deceleration
            const ease = 1 - Math.pow(1 - progress, 4);
            const current = ease * data.target;

            el.innerText = current.toFixed(data.decimals);

            if (progress < 1) {
                this.activeFrames.set(el, requestAnimationFrame(step));
            } else {
                el.innerText = data.raw; // Ensure precision at end
                this.activeFrames.delete(el);
            }
        };

        this.activeFrames.set(el, requestAnimationFrame(step));
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    new FoxtrotCounter({
        thresholdTrigger: 0.2, // 0.8 Start when 80% visible
        thresholdReset: 0.1,   // Reset when only 10% visible
        bufferTime: 300,       // 0.3s between numbers
        duration: 1500         // 1.5s total duration per number
    });
});